import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {ContextualMenuInputsOption} from '@Components/contextual-menu/components/contextual-menu-inputs-option';
import {DEFAULT_QR_MAX_LENGTH} from '@PosterWhiteboard/items/qr-code-item.class';
import {InputSuggestionsDropdown} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown';
import {useGetLinkInputSuggestions} from '@Hooks/poster-editor/useGetInputSuggestions';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from '@Components/input-suggestions-dropdown/input-suggestions-dropdown.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useAppSelector} from '@/hooks';
import {GA4EventName} from '@Libraries/ga-events';

export function QRContentInputOption(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemMessage = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId];

    if (item && 'message' in item) {
      return item.message;
    }
    return undefined;
  });
  const [qrCodeMessage, setQrCodeMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const linkSuggestions = useGetLinkInputSuggestions();

  if (!activeItem || (activeItem && !('message' in activeItem)) || itemMessage === undefined) {
    throw new Error(`QR Item doesn't have message property`);
  }

  useEffect((): void => {
    setQrCodeMessage(itemMessage);
  }, [itemMessage]);

  const updateMessage = (message: string): void => {
    activeItem.updateMessageContent(message);
  };

  const onChangeHandler = (updatedValue: string): void => {
    if (!isEditing) {
      setIsEditing(true);
    }
    setQrCodeMessage(updatedValue);
  };

  const onChangeEnded = (updatedValue: string): void => {
    if (updatedValue === '') {
      return;
    }
    if (isEditing) {
      setIsEditing(false);
    }
    updateMessage(updatedValue);
  };

  const getInputValue = (): string => {
    return isEditing ? qrCodeMessage : itemMessage;
  };

  const getInputSelector = (): ReactElement<any> => {
    return (
      <ContextualMenuInputsOption
        id="qr-content-input-option"
        maxLength={2000}
        errorText={window.i18next.t('pmwjs_qr_code_content_limit')}
        showErrorState={itemMessage.length > DEFAULT_QR_MAX_LENGTH}
        onInputChange={onChangeHandler}
        value={getInputValue()}
        onInputChangeEnded={onChangeEnded}
        elementTitle={window.i18next.t('pmwjs_link_placeholder_2')}
      />
    );
  };

  return (
    <InputSuggestionsDropdown
      dropdownId="qr-content-input-option-dropdown"
      inputSelector={getInputSelector()}
      onSuggestionSelect={(url: string) => {
        onChangeEnded(url);
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.BUSINESS_PROFILES_DESIGN_EDITOR_QR_CODE);
      }}
      primarySuggestionsList={linkSuggestions}
      popupPosition={DROPDOWN_POSITION.FIXED}
      popupAlignment={ALIGNMENT_TYPE.BOTTOM_END}
      popupClassName={styles.mediumWidth}
      inputValue={getInputValue()}
    />
  );
}
